import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { useAiFilters } from './AiFilterContext'
import { buildForecastSeries, getSiteWape } from './mockData'
import { ChartFrame, baseChart, tightGrid } from '../profiles/dashboard/ChartFrame'

const AiForecastView = () => {
  const { site, horizon, siteDef } = useAiFilters()
  const series = useMemo(
    () => buildForecastSeries(site.value, horizon.value, siteDef),
    [site.value, horizon.value, siteDef]
  )
  const siteWape = getSiteWape(site.label, siteDef)

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        ...baseChart,
        type: 'line',
        toolbar: { show: true },
        zoom: { enabled: true },
      },
      grid: tightGrid,
      stroke: { curve: 'smooth', width: [3, 2], dashArray: [0, 0] },
      dataLabels: { enabled: false },
      xaxis: {
        categories: series.categories,
        title: { text: 'Date' },
        labels: {
          rotate: -45,
          rotateAlways: series.horizonDays > 14,
          hideOverlappingLabels: true,
          trim: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`),
        },
        title: { text: 'Daily electricity (kWh)' },
      },
      colors: ['#3E97FF', '#50CD89'],
      legend: { position: 'top', horizontalAlign: 'left', fontSize: '13px' },
      markers: { size: 0, hover: { size: 5 } },
      annotations: {
        xaxis: series.categories
          .map((cat, i) =>
            series.isMatch[i]
              ? {
                  x: cat,
                  borderColor: '#F1416C',
                  label: {
                    text: 'Event',
                    style: { color: '#fff', background: '#F1416C', fontSize: '10px' },
                  },
                }
              : undefined
          )
          .filter((x): x is NonNullable<typeof x> => Boolean(x)),
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (v) =>
            v == null || Number.isNaN(Number(v))
              ? '—'
              : `${Math.round(Number(v)).toLocaleString()} kWh`,
        },
      },
    }),
    [series]
  )

  const chartSeries = useMemo(
    () => [
      { name: 'Predicted', data: series.predicted },
      {
        name: 'Actual',
        data: series.actual.map((v) => (v == null ? null : v)) as (number | null)[],
      },
    ],
    [series]
  )

  const matchDayTotal = series.predicted.reduce(
    (sum, v, i) => (series.isMatch[i] ? sum + v : sum),
    0
  )
  const totalHorizon = series.predicted.reduce((a, b) => a + b, 0)
  const nextMatchIdx = series.isMatch.findIndex((m, i) => m && series.actual[i] == null)
  const nextMatchKwh = nextMatchIdx >= 0 ? series.predicted[nextMatchIdx] : null
  const nextMatchDate = nextMatchIdx >= 0 ? series.fullDates[nextMatchIdx] : null

  const upliftOptions: ApexOptions = useMemo(
    () => ({
      chart: { ...baseChart, type: 'bar', toolbar: { show: false } },
      grid: tightGrid,
      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
      dataLabels: {
        enabled: true,
        formatter: (v) => `${Number(v).toFixed(1)}%`,
      },
      colors: ['#F1416C'],
      xaxis: { title: { text: 'Relative impact (%)' } },
      yaxis: { labels: { maxWidth: 140 } },
      legend: { show: false },
      tooltip: { y: { formatter: (v) => `${Number(v).toFixed(1)}%` } },
    }),
    []
  )

  return (
    <>
      <div className='row g-4 mb-5'>
        <div className='col-6 col-xl-3'>
          <div className='card h-100 border-0 shadow-sm'>
            <div className='card-body'>
              <div className='text-gray-500 fs-7 fw-semibold mb-1'>{horizon.value}-day total</div>
              <div className='fs-2hx fw-bold text-gray-900'>
                {Math.round(totalHorizon).toLocaleString()}
              </div>
              <div className='text-muted fs-8'>kWh · {site.label}</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-xl-3'>
          <div className='card h-100 border-0 shadow-sm'>
            <div className='card-body'>
              <div className='text-gray-500 fs-7 fw-semibold mb-1'>Event days in window</div>
              <div className='fs-2hx fw-bold text-gray-900'>
                {Math.round(matchDayTotal).toLocaleString()}
              </div>
              <div className='text-muted fs-8'>
                kWh · {series.isMatch.filter(Boolean).length} events
              </div>
            </div>
          </div>
        </div>
        <div className='col-6 col-xl-3'>
          <div className='card h-100 border-0 shadow-sm'>
            <div className='card-body'>
              <div className='text-gray-500 fs-7 fw-semibold mb-1'>Next event day</div>
              <div className='fs-2hx fw-bold text-gray-900'>
                {nextMatchKwh != null ? nextMatchKwh.toLocaleString() : '—'}
              </div>
              <div className='text-muted fs-8'>
                {nextMatchDate ? `${nextMatchDate} · kWh` : 'No event in horizon'}
              </div>
            </div>
          </div>
        </div>
        <div className='col-6 col-xl-3'>
          <div className='card h-100 border-0 shadow-sm'>
            <div className='card-body'>
              <div className='text-gray-500 fs-7 fw-semibold mb-1'>Backtest WAPE (h=7)</div>
              <div className='fs-2hx fw-bold text-gray-900'>
                {siteWape ? `${siteWape.wapeH7}%` : '—'}
              </div>
              <div className='text-muted fs-8'>{site.label}</div>
            </div>
          </div>
        </div>
      </div>

      <div className='card border-0 shadow-sm mb-5 overflow-hidden'>
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title fw-bold fs-4'>Daily forecast · {site.label}</h3>
          <div className='card-toolbar'>
            <span className='badge badge-light-primary'>
              Horizon {horizon.value}d · origin {series.origin}
            </span>
          </div>
        </div>
        <div className='card-body pt-0' style={{ minWidth: 0 }}>
          <ChartFrame>
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type='line'
              width='100%'
              height={380}
            />
          </ChartFrame>
        </div>
      </div>

      <div className='row g-4'>
        <div className='col-lg-6' style={{ minWidth: 0 }}>
          <div className='card border-0 shadow-sm h-100 overflow-hidden'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title fw-bold fs-4'>Site load profile</h3>
            </div>
            <div className='card-body pt-0' style={{ minWidth: 0 }}>
              <ChartFrame>
                <ReactApexChart
                  options={upliftOptions}
                  series={[
                    {
                      name: 'Event uplift',
                      data: [
                        { x: 'Base day', y: 0 },
                        {
                          x: 'Event day',
                          y: Math.round(siteDef.eventUplift * 1000) / 10,
                        },
                        {
                          x: 'Weekend factor',
                          y: Math.round((siteDef.weekendFactor - 1) * 1000) / 10,
                        },
                        {
                          x: 'Seasonality amp',
                          y: Math.round(siteDef.seasonalityAmp * 1000) / 10,
                        },
                      ],
                    },
                  ]}
                  type='bar'
                  width='100%'
                  height={280}
                />
              </ChartFrame>
            </div>
          </div>
        </div>
        <div className='col-lg-6'>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-header border-0 pt-6'>
              <h3 className='card-title fw-bold fs-4'>{site.label} · site metrics</h3>
            </div>
            <div className='card-body pt-0'>
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle gs-0 gy-3 mb-0'>
                  <tbody>
                    <tr>
                      <td className='text-muted'>Type</td>
                      <td className='fw-semibold text-capitalize text-end'>
                        {siteDef.type.replace(/_/g, ' ')}
                      </td>
                    </tr>
                    <tr>
                      <td className='text-muted'>Baseline kWh/day</td>
                      <td className='fw-semibold text-end'>{siteDef.baseKwh.toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td className='text-muted'>Event uplift</td>
                      <td className='fw-semibold text-end'>
                        {Math.round(siteDef.eventUplift * 100)}%
                      </td>
                    </tr>
                    <tr>
                      <td className='text-muted'>Weekend factor</td>
                      <td className='fw-semibold text-end'>{siteDef.weekendFactor}×</td>
                    </tr>
                    <tr>
                      <td className='text-muted'>Backtest WAPE h=7</td>
                      <td className='fw-semibold text-end'>
                        {siteWape ? `${siteWape.wapeH7}%` : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AiForecastView
